import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import ConfirmDialog from "app/shared/widgets/ConfirmDialog";
import tripLabelServices from "app/services/trip-label-services";
import orderServices from "app/services/order-services";
import HaulersLabelDialog from "app/pages/orders/HaulersLabelDialog";

// Etiquetas por imprimir de los viajes, por pedido y por tipo (Mercado Libre, Blue
// Express, Haulers). Se llega desde el menu o desde el icono QR de un viaje
// (?trip_id=). Por ahora solo superadmin.
//
// Imprimir usa los mismos endpoints que el listado de Pedidos: son ellos los que
// anotan la impresion, asi que da lo mismo desde donde se saque la etiqueta.

const KIND_COLORS = {
    meli: { bg: '#FFF159', fg: '#333' },
    bluexpress: { bg: '#0052cc', fg: '#fff' },
    haulers: { bg: '#7352C7', fg: '#fff' },
};

// Mercado Libre y Blue Express salen en lote (un PDF); la Haulers es de a un pedido
// porque pide la cantidad de bultos.
const BATCH_KINDS = ['meli', 'bluexpress'];

const fetchPdfByKind = {
    meli: (ids) => orderServices.fetchShipmentLabels(ids),
    bluexpress: (ids) => orderServices.fetchBluexpressLabels(ids),
};

// El backend contesta los errores como JSON aunque se haya pedido un blob.
const parseBackendError = async (err) => {
    const raw = err?.response?.data;
    if (!raw) return null;
    try {
        if (raw instanceof Blob) return JSON.parse(await raw.text());
        if (typeof raw === 'string') return JSON.parse(raw);
        if (typeof raw === 'object') return raw;
    } catch (_) { /* no es JSON */ }
    return null;
};

const printedText = (label) => {
    if (!label.printed) return 'Pendiente';
    const cuando = moment(label.last_printed_at).format('DD/MM HH:mm');
    const quien = label.last_printed_by ? ` por ${label.last_printed_by}` : '';
    const veces = label.print_count > 1 ? ` · ${label.print_count} veces` : '';
    return `Impresa ${cuando}${quien}${veces}`;
};

const TripLabelsPage = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const tripId = searchParams.get('trip_id');

    const today = moment().format('YYYY-MM-DD');
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [kindFilter, setKindFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('pending');

    const [kinds, setKinds] = useState([]);
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(null);
    const [confirmReprint, setConfirmReprint] = useState(null);
    const [haulersOrder, setHaulersOrder] = useState(null);

    const isSuperadmin = useMemo(
        () => (authUser?.profiles || []).some((profile) => profile.name === 'superadmin'),
        [authUser],
    );

    useEffect(() => {
        if (authUser && !isSuperadmin) navigate('/trips');
    }, [authUser, isSuperadmin, navigate]);

    const load = useCallback(() => {
        setLoading(true);
        tripLabelServices.getTripLabels(tripId ? { tripIds: [tripId] } : { from, to })
            .then((data) => {
                setKinds(data.kinds || []);
                setTrips(data.trips || []);
            })
            .catch(() => enqueueSnackbar('No se pudieron cargar las etiquetas', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [tripId, from, to, enqueueSnackbar]);

    useEffect(() => { if (isSuperadmin) load(); }, [isSuperadmin, load]);

    const kindLabel = useCallback(
        (key) => kinds.find((kind) => kind.key === key)?.label || key,
        [kinds],
    );

    // Los filtros se aplican aca: el desglose de un dia es chico y asi cambiar de
    // filtro no vuelve a pedir nada.
    const visibleTrips = useMemo(() => trips.map((trip) => ({
        ...trip,
        orders: trip.orders.map((order) => ({
            ...order,
            labels: order.labels.filter((label) => (
                (kindFilter === 'all' || label.kind === kindFilter)
                && (statusFilter === 'all' || !label.printed)
            )),
        })).filter((order) => order.labels.length > 0),
    })).filter((trip) => trip.orders.length > 0), [trips, kindFilter, statusFilter]);

    const pendingCount = (trip, kind) => trip.orders.reduce(
        (sum, order) => sum + order.labels.filter((l) => l.kind === kind && !l.printed).length, 0,
    );

    const printBatch = async (kind, orders) => {
        setPrinting(kind);
        try {
            const blob = await fetchPdfByKind[kind](orders.map((order) => order.id));
            const url = URL.createObjectURL(blob);
            const printWindow = window.open(url);
            if (printWindow) printWindow.onload = () => { printWindow.print(); };
            enqueueSnackbar(`Etiquetas de ${kindLabel(kind)} generadas`, { variant: 'success' });
        } catch (err) {
            const parsed = await parseBackendError(err);
            enqueueSnackbar(parsed?.error || `Error al obtener etiquetas de ${kindLabel(kind)}`, { variant: 'error' });
        }
        setPrinting(null);
        load();
    };

    // Una sola etiqueta: si ya se imprimio, se pide confirmacion antes de repetirla.
    const requestPrint = (order, label) => {
        const run = () => {
            if (label.kind === 'haulers') setHaulersOrder(order);
            else printBatch(label.kind, [order]);
        };
        if (label.printed) {
            setConfirmReprint({
                message: `La etiqueta de ${kindLabel(label.kind)} del pedido ${order.code} ya se imprimió (${printedText(label).replace('Impresa ', '')}). ¿Imprimirla de nuevo?`,
                run,
            });
        } else {
            run();
        }
    };

    const printPendingOfTrip = (trip, kind) => {
        const orders = trip.orders.filter((order) => order.labels.some((l) => l.kind === kind && !l.printed));
        if (orders.length > 0) printBatch(kind, orders);
    };

    const renderLabel = (order, label) => {
        const colors = KIND_COLORS[label.kind] || {};
        return (
            <Stack key={label.kind} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                <Chip
                    size="small"
                    label={kindLabel(label.kind)}
                    sx={{ bgcolor: colors.bg, color: colors.fg, fontWeight: 500, minWidth: 110 }}
                />
                <Typography
                    variant="body2"
                    color={label.printed ? 'success.main' : 'text.secondary'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}
                >
                    {label.printed && <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    {printedText(label)}
                </Typography>
                <Tooltip title={label.printed ? 'Reimprimir' : 'Imprimir'} arrow>
                    <span>
                        <Button
                            size="small"
                            variant={label.printed ? 'text' : 'outlined'}
                            startIcon={<PrintOutlinedIcon fontSize="small" />}
                            disabled={Boolean(printing)}
                            onClick={() => requestPrint(order, label)}
                        >
                            {label.printed ? 'Reimprimir' : 'Imprimir'}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
        );
    };

    const renderTrip = (trip) => (
        <Card key={trip.id} sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ mb: 1 }}>
                    <Box>
                        <Typography variant="h4" color="primary">Viaje Nº {trip.code}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {trip.ship_date ? moment(trip.ship_date).format('DD/MM/YYYY') : 'Sin fecha'}
                            {trip.driver_name ? ` · ${trip.driver_name}` : ''}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {BATCH_KINDS.filter((kind) => pendingCount(trip, kind) > 0
                            && (kindFilter === 'all' || kindFilter === kind)).map((kind) => (
                            <Button
                                key={kind}
                                size="small"
                                variant="contained"
                                startIcon={printing === kind ? <CircularProgress size={14} color="inherit" /> : <PrintOutlinedIcon />}
                                disabled={Boolean(printing)}
                                onClick={() => printPendingOfTrip(trip, kind)}
                            >
                                {`Imprimir ${kindLabel(kind)} pendientes (${pendingCount(trip, kind)})`}
                            </Button>
                        ))}
                    </Stack>
                </Stack>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 160 }}>Pedido</TableCell>
                            <TableCell sx={{ width: 240 }}>Cliente</TableCell>
                            <TableCell>Etiquetas</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trip.orders.map((order) => (
                            <TableRow key={order.id}>
                                <TableCell sx={{ fontWeight: 500 }}>{order.code}</TableCell>
                                <TableCell>{order.customer_name || '-'}</TableCell>
                                <TableCell>{order.labels.map((label) => renderLabel(order, label))}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );

    const emptyText = () => {
        if (kinds.length === 0) return 'Esta empresa no tiene integraciones de etiquetas (Mercado Libre, Blue Express o etiqueta Haulers).';
        if (statusFilter === 'pending') return 'No hay etiquetas pendientes con estos filtros.';
        return 'No hay etiquetas con estos filtros.';
    };

    return (
        <React.Fragment>
            <ConfirmDialog
                open={Boolean(confirmReprint)}
                title="Esta etiqueta ya se imprimió"
                message={confirmReprint?.message}
                confirmText="Imprimir de nuevo"
                cancelText="Cancelar"
                onClose={() => setConfirmReprint(null)}
                onConfirm={() => { const { run } = confirmReprint; setConfirmReprint(null); run(); }}
            />

            <HaulersLabelDialog
                isOpen={Boolean(haulersOrder)}
                order={haulersOrder}
                onClose={() => setHaulersOrder(null)}
                onPrinted={() => load()}
            />

            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <QrCode2Icon color="primary" />
                <Typography variant="h2">Etiquetas</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Las etiquetas que hay que imprimir en cada viaje, pedido por pedido. Cada una queda
                marcada al imprimirse, desde aquí o desde Pedidos.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                {tripId ? (
                    <Chip
                        color="primary"
                        label={`Solo el viaje ${trips[0]?.code ? `Nº ${trips[0].code}` : tripId}`}
                        onDelete={() => setSearchParams({})}
                    />
                ) : (
                    <>
                        <TextField
                            type="date"
                            size="small"
                            label="Desde"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="Hasta"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </>
                )}
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={kindFilter}
                    onChange={(_e, value) => value && setKindFilter(value)}
                >
                    <ToggleButton value="all">Todas</ToggleButton>
                    {kinds.map((kind) => (
                        <ToggleButton key={kind.key} value={kind.key}>{kind.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={statusFilter}
                    onChange={(_e, value) => value && setStatusFilter(value)}
                >
                    <ToggleButton value="pending">Pendientes</ToggleButton>
                    <ToggleButton value="all">Todas</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : visibleTrips.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {emptyText()}
                </Typography>
            ) : (
                visibleTrips.map(renderTrip)
            )}
        </React.Fragment>
    );
};

export default TripLabelsPage;
